import { useEffect, useState } from 'react';
import type { ReactNode } from 'react';
import { FormattedMessage } from 'react-intl';
import {
    Autocomplete,
    FormControl,
    Select,
    MenuItem,
    ListItemText,
    InputLabel,
    Chip,
    Input,
    Button,
    Checkbox,
    TextField,
    ListItem,
    Box,
    autocompleteClasses,
    inputClasses,
    inputLabelClasses,
    selectClasses,
    styled,
} from '@mui/material';
import { matchSorter } from 'match-sorter';
import { ArrowDownIcon } from '../icons';
import { LoadingIndicator } from '../LoadingIndicator';
import MetaResultsLoader from './MetaResultsLoader';
import { getResponseCountFromMetaResults } from './getResponseCountFromMetaResults';
import type { RespondentFilterUsingMetaResultsQuery$data } from './__generated__/RespondentFilterUsingMetaResultsQuery.graphql';

let nextId = 1;

const StyledChip = styled(Chip)(({ theme }) => ({
    margin: 0.5,
    color: theme.bino.color.white,
    backgroundColor: theme.bino.color.lightPurple,
    fontWeight: 'normal',
    '& svg': {
        color: theme.bino.color.palePurple,
    },
    '&:hover svg': {
        color: theme.bino.color.darkPurple,
    },
    '&:focus': {
        backgroundColor: theme.bino.color.darkPurple,
    },
    '&:focus:hover svg': {
        color: theme.bino.color.lightPurple,
    },
}));

const OptionLabelWithResponseCount = (props: {
    option: Option;
    responseCount: ReactNode;
}) => {
    return (
        <>
            <Box
                component="span"
                sx={(theme) => ({
                    '& + span': {
                        marginLeft: 3, // Set to "auto" to make it align right
                        color: theme.bino.color.gray,
                        fontSize: 12,
                    },
                })}
            >
                {props.option.label}
            </Box>
            <span> {props.responseCount}</span>
        </>
    );
};

type Option = {
    label: string;
    value: string;
};

interface MetaSelectProps {
    useAutocomplete?: boolean;
    options: ReadonlyArray<Option>;
    label: ReactNode;
    lightBackgroundColor?: boolean;
    metaKey: string;
    metaValues: ReadonlyArray<string>;
    metaResults: RespondentFilterUsingMetaResultsQuery$data['metaResults'];
    useMetaResultsLoader?: boolean;
    loadingMetaResults?: boolean;
    filters: ReadonlyArray<{
        metaKey: string;
        metaValues: ReadonlyArray<string>;
    }>;

    onChange(metaKey: string, metaValues: ReadonlyArray<string>): void;

    questionnaireId: string;
}

function MetaSelect(props: MetaSelectProps) {
    const [metaValues, setMetaValues] = useState(props.metaValues);
    const [metaResults, setMetaResults] = useState(props.metaResults);
    const [open, setOpen] = useState(false);
    const [loadingMetaResults, setLoadingMetaResults] = useState(false);
    const [shouldMountResultsLoader, setShouldMountResultsLoader] =
        useState(false);

    /**
     * whenever props.metaValues changes, we should also update the metaValues in state, but
     * not when the select dropdown is open.
     */
    useEffect(() => {
        if (!open && metaValues !== props.metaValues) {
            setMetaValues(props.metaValues);
        }
    }, [open, metaValues, props.metaValues]);

    /**
     * whenever props.metaResults changes, we should also update the metaResults in state
     *
     * (only applies when useMetaResultsLoader === false, since in that case, metaResults
     *  is handled externally (my using metaGroups) )
     */
    useEffect(() => {
        if (
            props.useMetaResultsLoader === false &&
            metaResults !== props.metaResults
        ) {
            setMetaResults(props.metaResults);
        }
    }, [props.useMetaResultsLoader, metaResults, props.metaResults]);

    /**
     * We need to identify this meta-select ?
     */
    const id = `MetaSelect-${nextId++}`;

    const onSelectChange = (event: any) => {
        const metaValues = event.target.value.filter(
            (value: any) => value !== null && value !== 'undefined',
        );
        setMetaValues(metaValues);
    };
    const getOptionLabel = (option: Option): string => {
        const responseCount = getOptionResponseCount(option);
        if (null === responseCount) {
            return option.label;
        }
        return `${option.label} (${responseCount})`;
    };

    const renderSelectValue = (values: any) => {
        const { options } = props;

        if (values.length === 0) {
            return null;
        }

        return (
            <Box display="flex" flexWrap="wrap">
                {options
                    .filter((option) => values.indexOf(option.value) > -1)
                    .map((option) => (
                        <StyledChip
                            key={option.value}
                            label={getOptionLabel(option)}
                            clickable
                            onMouseDown={(event) => event.stopPropagation()}
                            onDelete={(event) => {
                                event.stopPropagation();

                                props.onChange(
                                    props.metaKey,
                                    values.filter(
                                        (value: string) =>
                                            value !== option.value,
                                    ),
                                );
                            }}
                        />
                    ))}
            </Box>
        );
    };

    const onOpen = () => {
        const { useMetaResultsLoader = true } = props;
        setOpen(true);
        if (!shouldMountResultsLoader && useMetaResultsLoader) {
            setShouldMountResultsLoader(metaValues.length > 0);
        }
    };

    const onClose = (event: any) => {
        event.stopPropagation();

        // Don't call if no changes! (todo - deep compare, otherwise checking then unchecking also triggers onchange)
        if (props.metaValues !== metaValues) {
            props.onChange(props.metaKey, metaValues);
        }
        setOpen(false);
    };

    const onBeginLoadMetaResults = () => {
        setLoadingMetaResults(true);
    };

    const onLoadMetaResults = (metaResults: any) => {
        setLoadingMetaResults(false);
        setMetaResults(metaResults);
    };

    /**
     * Returns NULL when the results are not loaded
     */
    const getOptionResponseCount = (option: Option): number | null => {
        const { metaKey } = props;
        const selected = metaValues.includes(option.value);

        if ((loadingMetaResults && !selected) || loadingMetaResults) {
            return null;
        }

        return getResponseCountFromMetaResults(
            metaKey,
            option.value,
            metaResults,
        );
    };
    const renderLoadingResponseCount = (option: Option) => {
        const responseCount = getOptionResponseCount(option);
        if (responseCount === null) {
            return <LoadingIndicator size={22} delay={100} />;
        }
        return (
            <FormattedMessage
                defaultMessage="{responseCount,
                                                plural,
                                                one {# response}
                                                other {# responses}} "
                description="Number of responses for a meta value"
                values={{
                    responseCount,
                }}
            />
        );
    };

    const {
        options,
        label,
        questionnaireId,
        filters,
        lightBackgroundColor,
        useMetaResultsLoader = true,
        useAutocomplete = false,
    } = props;

    return (
        <>
            {shouldMountResultsLoader && (
                <MetaResultsLoader
                    filters={filters.filter((f) => f.metaKey !== props.metaKey)}
                    questionnaireId={questionnaireId}
                    shouldLoad={open}
                    onLoad={onLoadMetaResults}
                    onBeginLoad={onBeginLoadMetaResults}
                />
            )}
            {useAutocomplete ? (
                <Autocomplete<Option, true, false, false>
                    fullWidth
                    multiple
                    autoHighlight
                    disableCloseOnSelect
                    open={open}
                    onOpen={onOpen}
                    onClose={onClose}
                    value={options.filter((option) =>
                        metaValues.includes(option.value),
                    )}
                    onChange={(_event, options) => {
                        const metaValues = options.map((option) => {
                            return (option as Option).value;
                        });
                        if (open) {
                            setMetaValues(metaValues);
                        } else {
                            props.onChange(props.metaKey, metaValues);
                        }
                    }}
                    filterOptions={(options, { inputValue }) =>
                        matchSorter(
                            useMetaResultsLoader
                                ? options
                                : options.filter((option) => {
                                      const metaResult = metaResults.find(
                                          (metaResult) =>
                                              metaResult.metaKey ===
                                                  props.metaKey &&
                                              metaResult.metaValue ===
                                                  option.value,
                                      );
                                      return (
                                          (metaResult?.responseCount || 0) > 0
                                      );
                                  }),
                            inputValue,
                            {
                                keys: ['label'],
                            },
                        )
                    }
                    options={options}
                    getOptionLabel={getOptionLabel}
                    renderInput={(params) => (
                        <TextField
                            {...params}
                            margin="normal"
                            variant="standard"
                            label={label}
                            InputProps={{
                                ...params.InputProps,
                                sx: (theme) => ({
                                    [`&.${inputClasses.focused}:after`]: {
                                        borderColor: lightBackgroundColor
                                            ? undefined
                                            : theme.bino.color.palePurple,
                                    },
                                }),
                            }}
                            InputLabelProps={{
                                sx: (theme) => ({
                                    [`&.${inputLabelClasses.root}`]: {
                                        color: lightBackgroundColor
                                            ? theme.bino.color.gray
                                            : theme.bino.color.palePurple,
                                    },
                                    [`&.${inputLabelClasses.shrink}`]: {
                                        textTransform: 'uppercase',
                                    },
                                    [`&.${inputLabelClasses.focused}`]: {
                                        color: lightBackgroundColor
                                            ? undefined
                                            : theme.bino.color.palePurple +
                                              '!important',
                                    },
                                }),
                            }}
                        />
                    )}
                    renderOption={({ key, ...props }, o, { selected }) => (
                        <li key={key} {...props}>
                            <Checkbox checked={selected} />
                            <OptionLabelWithResponseCount
                                option={o}
                                responseCount={renderLoadingResponseCount(o)}
                            />
                        </li>
                    )}
                    renderTags={(tagValue, getTagProps) =>
                        tagValue.map((option, index) => (
                            // biome-ignore lint/correctness/useJsxKeyInIterable: getTagProps returns key
                            <StyledChip
                                label={getOptionLabel(option)}
                                {...getTagProps({ index })}
                            />
                        ))
                    }
                    popupIcon={<ArrowDownIcon size={12} />}
                    sx={(theme) => ({
                        [`& .${autocompleteClasses.popupIndicator}`]: {
                            color: theme.bino.color.palePurple,
                        },
                        [`& .${autocompleteClasses.clearIndicator}`]: {
                            color: lightBackgroundColor
                                ? theme.bino.color.gray
                                : theme.bino.color.palePurple,
                        },
                    })}
                />
            ) : (
                <FormControl fullWidth margin="normal">
                    <InputLabel
                        htmlFor={id}
                        shrink
                        sx={(theme) => ({
                            color: `${
                                lightBackgroundColor
                                    ? theme.bino.color.black
                                    : theme.bino.color.palePurple
                            } !important`,
                            textTransform: 'uppercase',
                        })}
                    >
                        {label}
                    </InputLabel>

                    <Select
                        multiple
                        displayEmpty
                        value={metaValues}
                        renderValue={renderSelectValue}
                        onChange={onSelectChange}
                        input={
                            <Input
                                id={id}
                                sx={(theme) => ({
                                    [`&.${inputClasses.underline}:hover::before`]:
                                        {
                                            borderColor: `${
                                                lightBackgroundColor
                                                    ? theme.bino.color.black
                                                    : theme.bino.color
                                                          .darkPurple
                                            }!important`,
                                        },
                                    [`&.${inputClasses.underline}::before`]: {
                                        borderColor: lightBackgroundColor
                                            ? theme.bino.color.lightGray
                                            : theme.bino.color.lightPurple,
                                    },
                                })}
                            />
                        }
                        IconComponent={ArrowDownIcon}
                        onOpen={onOpen}
                        onClose={onClose}
                        open={open}
                        sx={(theme) => ({
                            [`&.${selectClasses.select}`]: {
                                minHeight: '1vh',
                            },
                            [`& .${selectClasses.icon}`]: {
                                color: theme.bino.color.palePurple,
                                height: 8,
                                top: '50%',
                            },
                        })}
                    >
                        {options
                            // Only keep options with at least one response
                            .filter((o) => {
                                if (useMetaResultsLoader) {
                                    return true;
                                }
                                const metaResult = metaResults.find(
                                    (metaResult) =>
                                        metaResult.metaKey === props.metaKey &&
                                        metaResult.metaValue === o.value,
                                );
                                return (metaResult?.responseCount || 0) > 0;
                            })
                            .map((o) => (
                                <MenuItem key={o.value} value={o.value}>
                                    {' '}
                                    <Checkbox
                                        checked={
                                            metaValues.indexOf(o.value) > -1
                                        }
                                    />
                                    <ListItemText
                                        primary={
                                            <OptionLabelWithResponseCount
                                                option={o}
                                                responseCount={renderLoadingResponseCount(
                                                    o,
                                                )}
                                            />
                                        }
                                    />
                                </MenuItem>
                            ))}
                        {/*Does not quite work, since the Select assumes just MenuItems. */}
                        <ListItem
                            sx={(theme) => ({
                                color: theme.bino.color.label,
                                textAlign: 'center',
                                display: 'flex',
                                justifyContent: 'space-evenly',
                            })}
                        >
                            <Button onClick={onClose} fullWidth>
                                <FormattedMessage
                                    defaultMessage="Close"
                                    description="Close button"
                                />
                            </Button>
                            {/*<Button onClick={ this.onSelectAll }>All</Button>*/}
                            {/*<Button onClick={ this.onSelectNone }>None</Button>*/}
                        </ListItem>
                    </Select>
                </FormControl>
            )}
        </>
    );
}

export default MetaSelect;
